import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Bien } from '../models/models'; // Ensure to define Bien model
import api from './api';


interface BienFilters {
  nom: string | null;
  type: string | null;
  adresse: string | null;
  surface: number | null;
  proprietaire_id: number | null;
  groupe_bien_id: number | null;
  isDisponible: 0 | 1 | null;
   limit: number;
    offset: number;
}
interface BienState {
  bienData: Bien[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedBien: Bien | null;
  filters: BienFilters;
}

const initialState: BienState = {
  bienData: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  loading: false,
  error: null,
  successMessage: null,
  selectedBien: null,
  filters: {
    nom: null,
    type: null,
    adresse: null,
    surface: null,
    proprietaire_id: null,
    groupe_bien_id: null,
    isDisponible: null,
    limit: 5,
    offset: 0,
  }, // initialisé à vide
};

// Fetch all Bien with pagination
export const fetchBienData = createAsyncThunk<
  { data: Bien[]; total: number; totalPages: number; currentPage: number },
  {
    nom?: string | null;
    type?: string | null;
    adresse?: string | null;
    surface?: number | null;
    proprietaire_id?: number | null;
    groupe_bien_id?: number | null;
    isDisponible?: 0 | 1 | null;
    limit: number;
    offset: number;
  },
  { rejectValue: string }
>(
  'bien/fetchBienData',
  async (params, thunkAPI) => {
    try {
      const response = await api.get('/biens/getAll', {
        params
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to load data.');
    }
  }
);


export const fetchBienDataByNameOrByGroupeName = createAsyncThunk<
  { data: Bien[]; total: number; totalPages: number; currentPage: number },
  { nom: string; limit: number; offset: number },
  { rejectValue: string }
>(
  'bien/getByNameOrByGroupeName',
  async ({ nom, limit, offset }, thunkAPI) => {
    try {
      const response = await api.get('/biens/getByNameOrByGroupeName', { params: { nom, limit, offset } });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to load data.');
    }
  }
);

// Create or update a Bien
export const createBien = createAsyncThunk<
  Bien,
  Bien,
  { rejectValue: string }
>(
  'bien/createOrUpdateBien',
  async (bienData, thunkAPI) => {
    try {
      const response = await api.post('/biens/createOrUpdate', bienData);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to create or update.');
    }
  }
);

// Delete a Bien
export const deleteBien = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>(
  'bien/deleteBien',
  async (bienId, thunkAPI) => {
    try {
      await api.delete(`/biens`, { params: { id: bienId } });
      return bienId;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to delete.');
    }
  }
);

const bienSlice = createSlice({
  name: 'bien',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setSelectedBien: (state, action: PayloadAction<Bien | null>) => {
      state.selectedBien = action.payload;
    },
    clearBienData: (state) => {
      state.bienData = [];
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    setFilters: (state, action: PayloadAction<BienFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = {
    nom: null,
    type: null,
    adresse: null,
    surface: null,
    proprietaire_id: null,
    groupe_bien_id: null,
    isDisponible: null,
    limit: 5,
    offset: 0
      };
    },

  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBienData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBienData.fulfilled, (state, action) => {
        state.loading = false;
        state.bienData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchBienData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchBienDataByNameOrByGroupeName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBienDataByNameOrByGroupeName.fulfilled, (state, action) => {
        state.loading = false;
        state.bienData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchBienDataByNameOrByGroupeName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createBien.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createBien.fulfilled, (state, action: PayloadAction<Bien>) => {
        state.loading = false;
        state.successMessage = 'Creation/Update successful!';
      })
      .addCase(createBien.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteBien.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteBien.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.successMessage = 'Deletion successful!';
      })
      .addCase(deleteBien.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCurrentPage, setSelectedBien, clearMessages, clearBienData,setFilters,resetFilters } = bienSlice.actions;
export default bienSlice.reducer;
